// File: WorkspaceManager.tsx
import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";

import { Box, Button, Snackbar, Alert, Tabs, Tab, Grid, Paper, Typography, TextField } from "@mui/material";
import axios from "axios";
import { baseUrl } from "../../constants";
import WorkspaceTable from "../../components/Workspace/WorkspaceTable";
import DepartmentTable from "../../components/Workspace/DepartmentTable";
import PositionTable from "../../components/Workspace/PositionTable";
import CreateEditDialog from "../../components/Workspace/CreateEditDialog";
import CreateEditDialogUser from "../../components/Workspace/CreateEditDialogUser";
import DeleteDialog from "../../components/Workspace/DeleteDialog";
import WorkspaceTree from "../../components/Workspace/WorkspaceTree";
import {
  Department,
  Workspace,
  Position,
  UserPosition,
  UserProject,
  User,
} from "../../types/models";

const WorkspaceManager: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  
  // Settings state
  const [settings, setSettings] = useState({
    currency: 'USD',
    locale: 'en-US',
    weekStartDay: 1,
    hrProjectId: '',
    salesProjectId: '',
    supportProjectId: '',
    logisticsProjectId: '',
    financeProjectId: '',
    marketingProjectId: '',
  }); // 0 = table, 1 = tree

  // For full tree
  const [fullTreeData, setFullTreeData] = useState<{
    workspaces: Workspace[];
    departments: Department[];
    positions: Position[];
    userPosition: UserPosition[];
    userProjects: UserProject[];
    members: User[];
  }>({
    workspaces: [],
    departments: [],
    positions: [],
    userPosition: [],
    userProjects: [],
    members: [],
  });

  const theme = useTheme();

  const [expanded, setExpanded] = useState<string[]>([]);

  const fetchFullTree = async () => {
    try {
      const res = await axios.get(`${baseUrl}/workspaces/full-tree`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const workspaces: Workspace[] = res.data;

      // Extract departments with workspaceId
      const departments: Department[] = workspaces.flatMap((w) =>
        (w.departments ?? []).map((d) => ({
          ...d,
          workspaceId: w.id,
        }))
      );

      // Extract positions with departmentId
      const positions: Position[] = departments.flatMap((d) =>
        (d.positions ?? []).map((p) => ({
          ...p,
          departmentId: d.id,
        }))
      );

      // Extract userPositions with positionId
      const userPositions: UserPosition[] = positions.flatMap((p) =>
        (p.userPositions ?? []).map((up) => ({
          ...up,
          positionId: p.id,
        }))
      );

      // Optional: extract userProjects if needed (flattened structure)
      const userProjects: UserProject[] = departments.flatMap((d) =>
        (d.projects ?? []).flatMap((proj) =>
          (proj.userProjects ?? []).map((up) => ({
            ...up,
            projectId: proj.id,
          }))
        )
      );

      // Optional: extract members per department (already provided in backend)
      const members: User[] = departments.flatMap((d) => d.members ?? []);

      // Set data
      setFullTreeData({
        workspaces,
        departments,
        positions,
        userPosition: userPositions,
        // Optional: you may add userProjects or members if needed in UI
        userProjects,
        members,
      });

      // Auto-expand all nodes
      const expandedNodes = [
        ...workspaces.map((w) => `workspace-${w.id}`),
        ...departments.map((d) => `department-${d.id}`),
        ...positions.map((p) => `position-${p.id}`),
        ...userPositions.map((up) => `userposition-${up.id}`),
      ];

      setExpanded(expandedNodes);
    } catch (err) {
      showSnackbar("Failed to fetch full tree", "error");
    }
  };

  // ========== Data States ==========
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);

  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null
  );
  const [selectedDepartment, setSelectedDepartment] =
    useState<Department | null>(null);

  // ========== Create/Edit Dialog State ==========
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogType, setCreateDialogType] = useState<
    "workspace" | "department" | "position" | "project"
  >("workspace");
  const [createDialogName, setCreateDialogName] = useState("");
  const [createDialogDescription, setCreateDialogDescription] = useState("");

  const [editingItem, setEditingItem] = useState<{
    id: number;
    name: string;
    type: "workspace" | "department" | "position" | "project";
  } | null>(null);

  const [_, setCreateTarget] = useState<{
    id: number;
    name: string;
    type: "workspace" | "department" | "position" | "project";
  } | null>(null);

  const [createParentId, setCreateParentId] = useState<number>();

  const [userAddDialog, setUserAddDialog] = useState<{
    parentId: number;
    type: "project" | "position";
  } | null>(null);

  const handleAdd = (parent: {
    id: number;
    name: string;
    type: "workspace" | "department" | "position" | "project";
  }) => {
    setCreateTarget(parent);
    setCreateParentId(parent.id);
    setCreateDialogType(
      parent.type === "workspace"
        ? "department"
        : parent.type === "department"
        ? "position"
        : "project" // ← if you're supporting user-projects
    );
    setCreateDialogName(""); // clear name input
    setCreateDialogDescription(""); // clear description input if any
    setCreateDialogOpen(true);
  };

  // ========== Delete Dialog ==========
  const [__, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  type DeleteTarget =
    | { id: number; type: "workspace" | "department" | "position" | "project" }
    | { type: "userposition"; userId: number; positionId: number }
    | { type: "userproject"; userId: number; projectId: number };

  const onDelete = (item: DeleteTarget) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleEditingNameChange = (newName: string) => {
    setEditingItem((prev) => (prev ? { ...prev, name: newName } : null));
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [loading, setLoading] = useState({
    workspaces: false,
    departments: false,
    positions: false,
  });

  const accessToken = localStorage.getItem("accessToken");

  // ========== API ==========
  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const showSnackbar = (message: string, severity: "success" | "error") =>
    setSnackbar({ open: true, message, severity });

  const fetchWorkspaces = async () => {
    try {
      setLoading((l) => ({ ...l, workspaces: true }));
      const res = await axios.get(`${baseUrl}/workspaces`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setWorkspaces(res.data);
    } catch {
      showSnackbar("Failed to fetch workspaces", "error");
    } finally {
      setLoading((l) => ({ ...l, workspaces: false }));
    }
  };

  const fetchDepartments = async (workspaceId: number) => {
    try {
      setLoading((l) => ({ ...l, departments: true }));
      const res = await axios.get(`${baseUrl}/departments/${workspaceId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setDepartments(res.data);
      setSelectedWorkspace(
        workspaces.find((w) => w.id === workspaceId) || null
      );
      setSelectedDepartment(null);
      setPositions([]);
    } catch {
      showSnackbar("Failed to fetch departments", "error");
    } finally {
      setLoading((l) => ({ ...l, departments: false }));
    }
  };

  const fetchPositions = async (departmentId: number) => {
    try {
      setLoading((l) => ({ ...l, positions: true }));
      const res = await axios.get(`${baseUrl}/positions/${departmentId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setPositions(res.data);
      setSelectedDepartment(
        departments.find((d) => d.id === departmentId) || null
      );
    } catch {
      showSnackbar("Failed to fetch positions", "error");
    } finally {
      setLoading((l) => ({ ...l, positions: false }));
    }
  };

  // ========== CRUD Logic ==========
  const handleCreate = async () => {
    if (!createDialogName.trim()) {
      return showSnackbar("Name is required", "error");
    }

    try {
      const headers = { Authorization: `Bearer ${accessToken}` };

      if (createDialogType === "workspace") {
        await axios.post(
          `${baseUrl}/workspaces`,
          {
            name: createDialogName,
            description: createDialogDescription,
          },
          { headers }
        );
      } else if (createDialogType === "department") {
        if (!createParentId)
          throw new Error("Missing workspaceId for department");
        await axios.post(
          `${baseUrl}/departments`,
          {
            name: createDialogName,
            workspaceId: createParentId,
          },
          { headers }
        );
      } else if (createDialogType === "position") {
        if (!createParentId)
          throw new Error("Missing departmentId for position");
        await axios.post(
          `${baseUrl}/positions`,
          {
            name: createDialogName,
            departmentId: createParentId,
          },
          { headers }
        );
      } else if (createDialogType === "project") {
        if (!createParentId)
          throw new Error("Missing departmentId for project");
        await axios.post(
          `${baseUrl}/projects`,
          {
            name: createDialogName,
            departmentId: createParentId,
          },
          { headers }
        );
      } else if (createDialogType === "userposition") {
        if (!createParentId)
          throw new Error("Missing positionId for userPosition");
        const defaultUserId = 1;
        await axios.post(
          `${baseUrl}/userpositions`,
          {
            positionId: createParentId,
            userId: defaultUserId,
          },
          { headers }
        );
      }

      setCreateDialogOpen(false);
      setCreateDialogName("");
      setCreateDialogDescription("");
      showSnackbar(`${createDialogType} created successfully`, "success");

      // Refresh everything
      if (tabIndex === 1) {
        fetchFullTree();
      } else {
        fetchWorkspaces();
        if (createDialogType === "department")
          fetchDepartments(createParentId!);
        if (createDialogType === "position") fetchPositions(createParentId!);
        // optionally fetch departments again for project if needed
      }
    } catch (err) {
      console.error(err);
      showSnackbar(`Failed to create ${createDialogType}`, "error");
    }
  };

  const handleEdit = async () => {
    if (!editingItem) return;

    try {
      const payload: any = {
        name: editingItem.name,
      };

      if (editingItem.type === "department" && selectedWorkspace) {
        payload.workspaceId = selectedWorkspace.id;
      }

      if (editingItem.type === "position" && selectedDepartment) {
        payload.departmentId = selectedDepartment.id;
      }

      if (editingItem.type === "project" && selectedDepartment) {
        payload.departmentId = selectedDepartment.id;
      }

      await axios.patch(
        `${baseUrl}/${editingItem.type}s/${editingItem.id}`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      // Refresh relevant data
      if (editingItem.type === "workspace") {
        fetchWorkspaces();
      } else if (editingItem.type === "department" && selectedWorkspace) {
        fetchDepartments(selectedWorkspace.id);
      } else if (editingItem.type === "position" && selectedDepartment) {
        fetchPositions(selectedDepartment.id);
      } else if (editingItem.type === "project") {
        // or a fetchProjects() method if you have one
      }

      fetchFullTree();

      showSnackbar("Updated successfully", "success");
      setEditingItem(null);
    } catch {
      showSnackbar("Update failed", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      if (
        deleteTarget.type === "userposition" &&
        deleteTarget.userId &&
        deleteTarget.positionId
      ) {
        await axios.delete(
          `${baseUrl}/positions/${deleteTarget.positionId}/users/${deleteTarget.userId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
      } else if (
        deleteTarget.type === "userproject" &&
        deleteTarget.userId &&
        deleteTarget.projectId
      ) {
        await axios.delete(
          `${baseUrl}/projects/${deleteTarget.projectId}/users/${deleteTarget.userId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
      } else if (
        deleteTarget.type === "workspace" ||
        deleteTarget.type === "department" ||
        deleteTarget.type === "position" ||
        deleteTarget.type === "project" // ✅ added support for deleting projects
      ) {
        await axios.delete(
          `${baseUrl}/${deleteTarget.type}s/${deleteTarget.id}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
      }

      // ✅ Refresh full tree instead of partial refresh
      await fetchFullTree();
      setSelectedWorkspace(null);
      setSelectedDepartment(null);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      showSnackbar("Deleted successfully", "success");
    } catch {
      showSnackbar("Delete failed", "error");
    }
  };

  // Settings update function
  const updateSetting = (k: string, v: any) => setSettings((s:any)=>({ ...s, [k]: v }));

  return (
    <Box sx={{ p: 2 }}>

      <Tabs
        value={tabIndex}
        onChange={(_, newValue) => {
          setTabIndex(newValue);
          if (newValue === 1) fetchFullTree(); // Load full tree when switching to tab 1
        }}
        sx={{ mb: 2 }}
      >
        <Tab label="Table View" />
        <Tab label="Tree View" />
        <Tab label="Workspace Settings" />
      </Tabs>

      {tabIndex === 0 && (
        <>
          <Button
            variant="contained"
            sx={{ mb: 2 }}
            onClick={() => {
              setCreateDialogType("workspace");
              setCreateDialogOpen(true);
            }}
          >
            Add Workspace
          </Button>

          <WorkspaceTable
            workspaces={workspaces}
            selectedWorkspace={selectedWorkspace}
            setSelectedWorkspace={setSelectedWorkspace}
            fetchDepartments={fetchDepartments}
            loading={loading.workspaces}
            showSnackbar={showSnackbar}
            setDeleteTarget={setDeleteTarget}
            setDeleteDialogOpen={setDeleteDialogOpen}
            setCreateDialogOpen={() => {
              setCreateDialogType("workspace");
              setCreateDialogOpen(true);
            }}
            setEditingItem={setEditingItem}
          />

          {selectedWorkspace && (
            <DepartmentTable
              departments={departments}
              selectedDepartment={selectedDepartment}
              setSelectedDepartment={setSelectedDepartment}
              fetchDepartments={fetchDepartments}
              fetchPositions={fetchPositions}
              workspaceId={selectedWorkspace.id}
              loading={loading.departments}
              showSnackbar={showSnackbar}
              setDeleteTarget={setDeleteTarget}
              setDeleteDialogOpen={setDeleteDialogOpen}
              setCreateDialogOpen={() => {
                setCreateDialogType("department");
                setCreateDialogOpen(true);
              }}
              setEditingItem={setEditingItem}
            />
          )}

          {selectedDepartment && (
            <PositionTable
              positions={positions}
              departmentId={selectedDepartment.id}
              fetchPositions={fetchPositions}
              loading={loading.positions}
              showSnackbar={showSnackbar}
              setDeleteTarget={setDeleteTarget}
              setDeleteDialogOpen={setDeleteDialogOpen}
              setCreateDialogOpen={() => {
                setCreateDialogType("position");
                setCreateDialogOpen(true);
              }}
              setEditingItem={setEditingItem}
            />
          )}
        </>
      )}
      {tabIndex === 1 && (
        <WorkspaceTree
          workspaces={fullTreeData.workspaces}
          departments={fullTreeData.departments}
          positions={fullTreeData.positions}
          userPositions={fullTreeData.userPosition}
          selectedWorkspace={selectedWorkspace}
          selectedDepartment={selectedDepartment}
          onSelectWorkspace={(id) => {
            const workspace =
              fullTreeData.workspaces.find((w) => w.id === id) || null;
            setSelectedWorkspace(workspace);
            // No fetch here — we're using full tree
            setExpanded((prev) => [...new Set([...prev, `workspace-${id}`])]);
          }}
          onSelectDepartment={(id) => {
            const department =
              fullTreeData.departments.find((d) => d.id === id) || null;
            setSelectedDepartment(department);
            setExpanded((prev) => [...new Set([...prev, `department-${id}`])]);
          }}
          onEdit={setEditingItem}
          expanded={expanded}
          setExpanded={setExpanded}
          onRefresh={fetchFullTree}
          onDelete={onDelete}
          onAdd={handleAdd}
          onAddUser={(parent) => setUserAddDialog(parent)}
        />
      )}

      {/* Workspace Settings Tab */}
      {tabIndex === 2 && (
        <Box>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Workspace Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }} variant="outlined">
                <Typography fontWeight={700} gutterBottom>
                  Defaults
                </Typography>
                <Box display="grid" gap={2}>
                  <TextField 
                    label="Currency" 
                    value={settings.currency} 
                    onChange={(e) => updateSetting('currency', e.target.value)} 
                  />
                  <TextField 
                    label="Locale" 
                    value={settings.locale} 
                    onChange={(e) => updateSetting('locale', e.target.value)} 
                  />
                  <TextField 
                    label="Week Start Day (0=Sun,1=Mon)" 
                    type="number" 
                    value={settings.weekStartDay} 
                    onChange={(e) => updateSetting('weekStartDay', Number(e.target.value))} 
                  />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }} variant="outlined">
                <Typography fontWeight={700} gutterBottom>
                  Default Projects
                </Typography>
                <Box display="grid" gap={2}>
                  {['hrProjectId','salesProjectId','supportProjectId','logisticsProjectId','financeProjectId','marketingProjectId'].map(k => (
                    <TextField 
                      key={k} 
                      label={k} 
                      value={(settings as any)[k]} 
                      onChange={(e) => updateSetting(k, e.target.value)} 
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box mt={2} display="flex" gap={1}>
            <Button variant="outlined">Reset</Button>
            <Button 
              variant="contained" 
              onClick={() => alert('TODO: wire to workspaceSettings API')}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {/* Create Dialog */}
      <CreateEditDialog
        open={createDialogOpen}
        type={createDialogType}
        mode="create" // ✅ Add this
        name={createDialogName}
        description={
          createDialogType === "workspace" ? createDialogDescription : undefined
        }
        setName={setCreateDialogName}
        setDescription={
          createDialogType === "workspace"
            ? setCreateDialogDescription
            : undefined
        }
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleCreate}
        parentId={createParentId} // optional if used in the dialog
      />

      {/* Edit Dialog */}
      <CreateEditDialog
        open={!!editingItem}
        type={editingItem?.type || "workspace"}
        mode="edit" // ✅ Add this
        name={editingItem?.name || ""}
        setName={handleEditingNameChange}
        onClose={() => setEditingItem(null)}
        onSave={handleEdit}
      />

      {userAddDialog && (
        <CreateEditDialogUser
          open={!!userAddDialog}
          parentId={userAddDialog.parentId}
          type={userAddDialog.type}
          onClose={() => setUserAddDialog(null)}
          onSuccess={fetchFullTree}
          showSnackbar={showSnackbar} // ✅ pass the Snackbar handler
        />
      )}

      <DeleteDialog
        open={!!deleteTarget}
        handleClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        type={deleteTarget?.type || "item"}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          sx={{
            bgcolor:
              snackbar.severity === "error"
                ? theme.palette.error.main
                : theme.palette.success.main,
            color: theme.palette.getContrastText(
              snackbar.severity === "error"
                ? theme.palette.error.main
                : theme.palette.success.main
            ),
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkspaceManager;
